/** Builders-list fetching — the same stale-while-revalidate cache + in-flight
 *  dedupe as useClientsList (see that file for the rationale): one cache entry
 *  at module scope, so builder → back → list paints instantly and revalidates
 *  in the background. reload() invalidates + revalidates after a create. */

import { useCallback, useEffect, useState } from "react";
import { buildersApi, ApiError } from "@/api";
import type { BuildersListResponse } from "@/types";

const DEDUP_WINDOW = 2_000;

interface CacheEntry {
  data: BuildersListResponse;
  fetchedAt: number;
}

let cache: CacheEntry | undefined;
let inflight: Promise<BuildersListResponse> | undefined;

function fetchDeduped(): Promise<BuildersListResponse> {
  if (inflight) return inflight;
  const p = buildersApi
    .listBuilders()
    .then((res) => {
      cache = { data: res, fetchedAt: Date.now() };
      return res;
    })
    .finally(() => {
      inflight = undefined;
    });
  inflight = p;
  return p;
}

function isWithinDedupWindow(): boolean {
  return !!cache && Date.now() - cache.fetchedAt < DEDUP_WINDOW;
}

export interface UseBuildersListResult {
  rows: BuildersListResponse["items"];
  total: number;
  loading: boolean;
  refreshing: boolean;
  error?: string;
  reload: () => void;
}

interface State {
  data?: BuildersListResponse;
  loading: boolean;
  refreshing: boolean;
  error?: string;
}

export function useBuildersList(): UseBuildersListResult {
  const [state, setState] = useState<State>(() =>
    cache
      ? { data: cache.data, loading: false, refreshing: !isWithinDedupWindow() }
      : { loading: true, refreshing: false },
  );
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => {
    cache = undefined;
    setReloadTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (cache) {
      if (isWithinDedupWindow()) {
        setState({ data: cache.data, loading: false, refreshing: false });
        return;
      }
      setState({ data: cache.data, loading: false, refreshing: true });
    } else {
      setState((s) =>
        s.data ? { data: s.data, loading: false, refreshing: true } : { loading: true, refreshing: false },
      );
    }
    fetchDeduped()
      .then((res) => {
        if (!cancelled) setState({ data: res, loading: false, refreshing: false });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : "Could not load builders.";
        setState((s) => ({ data: s.data, loading: false, refreshing: false, error: message }));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return {
    rows: state.data?.items ?? [],
    total: state.data?.total ?? 0,
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
    reload,
  };
}
